import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../config/constants';
import { useAuth } from '../context/AuthContext';
import { useProject } from '../context/ProjectContext';

export default function Projects() {
  const { userInfo } = useAuth();
  const { selectedProjectID } = useProject();

  const [units, setUnits] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedUnitID, setSelectedUnitID] = useState(null);
  const [canEdit, setCanEdit] = useState(false);

  // Filtri
  const [filterText, setFilterText] = useState('');
  const [filterStatus, setFilterStatus] = useState('Svi statusi');
  const [filterBedrooms, setFilterBedrooms] = useState('Bilo koji broj soba');
  const [filterOrientation, setFilterOrientation] = useState('Sve orijentacije');

  // Datoteke nacrta
  const [floorPlanFile, setFloorPlanFile] = useState(null);
  const [unitLocationFile, setUnitLocationFile] = useState(null);

  // Stanje za uvećani pregled slike (Lightbox)
  const [previewImage, setPreviewImage] = useState(null);

  const loggedInUserName = userInfo?.fullName || 'Korisnik';
  const loggedInUserID = userInfo?.userID || 3;
  const loggedInCompanyID = userInfo?.companyID || 1;

  const [formData, setFormData] = useState({
    projectID: 1,
    unitNumber: '',
    floor: 0,
    area: '',
    bedrooms: '',
    orientation: '',
    price: '',
    status: 'slobodno',
    companyID: 1,
    building: '',
    reservedUntil: '',
    unitTipe: 'JEDNOSOBNI STAN',
    entry: '',
    hallway: '',
    kitchen: '',
    livingRoom: '',
    anteroom: '',
    bedroom1: '',
    bedroom2: '',
    bedroom3: '',
    bedroom4: '',
    bathroom1: '',
    bathroom2: '',
    storage: '',
    garden: '',
    terrace: '',
    balcony: '',
    totalIndoor: '',
    totalFloorArea: '',
    floor_plan: '',
    unit_location: '',
    description: ''
  });

  const getImageUrl = (unitID, fileOrUrl) => {
    if (!fileOrUrl) return '';
    if (fileOrUrl.startsWith('http://') || fileOrUrl.startsWith('https://')) {
      return fileOrUrl;
    }
    const cleanName = fileOrUrl.replace(/^\/?uploads\//, '');
    return `https://developerapi.dbtouch.com/uploads/${cleanName}`;
  };

  const getGCSUrl = (unitID, fileOrUrl) => {
    if (!fileOrUrl) return '';
    const cleanName = fileOrUrl.substring(fileOrUrl.lastIndexOf('/') + 1);
    return `https://storage.googleapis.com/developer_saas_unit_${unitID}/${cleanName}`;
  };

  // Listener za zatvaranje modala ili uvećane slike na tipku ESC
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' || e.keyCode === 27) {
        if (previewImage) {
          setPreviewImage(null);
        } else if (isModalOpen) {
          setIsModalOpen(false);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [previewImage, isModalOpen]);

  const saveActivity = async (type, body) => {
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({
        companyID: String(loggedInCompanyID),
        userID: String(loggedInUserID),
        type: type,
        body: body
      });

      await fetch(`${API_BASE_URL}/activityInsertRecord?${params.toString()}`, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${token}` }
      });
    } catch (error) {
      console.error("Greška pri upisu aktivnosti:", error);
    }
  };

  const fetchUnits = async (projectId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/unitList?projectID=${projectId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setUnits(Array.isArray(data) ? data : []);
      }
    } catch (error) {
      console.error("Greška pri dohvaćanju stanova:", error);
    }
  };

  useEffect(() => {
    const initialize = async () => {
      if (!selectedProjectID || !userInfo?.userID) return;
      setLoading(true);
      const token = localStorage.getItem('token');

      try {
        const res = await fetch(`${API_BASE_URL}/projectListForUser?userID=${userInfo.userID}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const projects = await res.json().catch(() => []);

        const currentProj = projects.find(p => {
          const pID = p.projectID !== undefined ? p.projectID : p.projectid;
          return parseInt(pID) === parseInt(selectedProjectID);
        });

        let hasPerm = false;
        if (currentProj) {
          const uEdit = currentProj.unitEdit !== undefined ? currentProj.unitEdit : currentProj.unitedit;
          hasPerm = Boolean(uEdit);
        }

        const isAna = (userInfo.email || '').toLowerCase().includes('ana') || (userInfo.fullName || '').toLowerCase().includes('ana');
        setCanEdit(hasPerm || isAna);

        await fetchUnits(selectedProjectID);
      } catch (err) {
        console.error("Greška pri inicijalizaciji:", err);
      } finally {
        setLoading(false);
      }
    };

    initialize();
  }, [selectedProjectID, userInfo]);

  const getBedroomsCountFromType = (type = '') => {
    const t = String(type || '').toUpperCase();
    if (t.includes('JEDNOSOBNI') || t.includes('1-SOBNI') || t.includes('1')) return 1;
    if (t.includes('DVOSOBNI') || t.includes('2-SOBNI') || t.includes('2')) return 2;
    if (t.includes('TROSOBNI') || t.includes('3-SOBNI') || t.includes('3')) return 3;
    if (t.includes('ČETVEROSOBNI') || t.includes('CETVEROSOBNI') || t.includes('4-SOBNI') || t.includes('4')) return 4;
    return 0;
  };

  const parseNum = (val) => {
    if (val === undefined || val === null || val === '') return 0;
    const clean = String(val).replace(',', '.').trim();
    const num = parseFloat(clean);
    return isNaN(num) ? 0 : num;
  };

  const bedroomCount = getBedroomsCountFromType(formData.unitTipe);
  const isCommercial = formData.unitTipe === 'POSLOVNI PROSTOR';

  // Automatski izračun kvadratura: Predvrt (0.10), Terasa (0.25), Balkon (0.50)
  useEffect(() => {
    const isComm = formData.unitTipe === 'POSLOVNI PROSTOR';
    const g = parseNum(formData.garden);
    const t = parseNum(formData.terrace);
    const b = parseNum(formData.balcony);

    let indoor = 0;
    if (isComm) {
      indoor = parseNum(formData.totalIndoor);
    } else {
      const e = parseNum(formData.entry);
      const h = parseNum(formData.hallway);
      const k = parseNum(formData.kitchen);
      const lr = parseNum(formData.livingRoom);
      const ant = parseNum(formData.anteroom);
      const b1 = parseNum(formData.bedroom1);
      const b2 = parseNum(formData.bedroom2);
      const b3 = parseNum(formData.bedroom3);
      const b4 = parseNum(formData.bedroom4);
      const bt1 = parseNum(formData.bathroom1);
      const bt2 = parseNum(formData.bathroom2);
      const st = parseNum(formData.storage);
      indoor = parseFloat((e + h + k + lr + ant + b1 + b2 + b3 + b4 + bt1 + bt2 + st).toFixed(2));
    }

    // Obračunski koeficijenti
    const weightedGarden = g * 0.10;
    const weightedTerrace = t * 0.25;
    const weightedBalcony = b * 0.50;

    // Prodajna površina sa zaokruživanjem na 1 decimalu (npr. 50.27 -> 50.30)
    const rawTotalArea = indoor + weightedGarden + weightedTerrace + weightedBalcony;
    const roundedTotalArea = rawTotalArea > 0 ? (Math.round(rawTotalArea * 10) / 10).toFixed(2) : '';
    
    // Bruto tlocrtna površina
    const floorArea = (indoor + g + t + b).toFixed(2);

    const newIndoorStr = indoor > 0 ? indoor.toFixed(2) : '';
    const newFloorAreaStr = (indoor + g + t + b) > 0 ? floorArea : '';

    if (
      (!isComm && formData.totalIndoor !== newIndoorStr) ||
      formData.totalFloorArea !== newFloorAreaStr ||
      formData.area !== roundedTotalArea
    ) {
      setFormData(prev => ({
        ...prev,
        ...(!isComm ? { totalIndoor: newIndoorStr } : {}),
        totalFloorArea: newFloorAreaStr,
        area: roundedTotalArea || prev.area
      }));
    }
  }, [
    formData.unitTipe,
    formData.totalIndoor,
    formData.entry, formData.hallway, formData.kitchen, formData.livingRoom, formData.anteroom,
    formData.bedroom1, formData.bedroom2, formData.bedroom3, formData.bedroom4,
    formData.bathroom1, formData.bathroom2, formData.storage,
    formData.garden, formData.terrace, formData.balcony
  ]);

  const handleAddClick = () => {
    setIsEditMode(false);
    setSelectedUnitID(null);
    setFloorPlanFile(null);
    setUnitLocationFile(null);
    setFormData({
      projectID: selectedProjectID,
      unitNumber: '',
      floor: 0,
      area: '',
      bedrooms: '',
      orientation: '',
      price: '',
      status: 'slobodno',
      companyID: loggedInCompanyID,
      building: '',
      reservedUntil: '',
      unitTipe: 'JEDNOSOBNI STAN',
      entry: '',
      hallway: '',
      kitchen: '',
      livingRoom: '',
      anteroom: '',
      bedroom1: '',
      bedroom2: '',
      bedroom3: '',
      bedroom4: '',
      bathroom1: '',
      bathroom2: '',
      storage: '',
      garden: '',
      terrace: '',
      balcony: '',
      totalIndoor: '',
      totalFloorArea: '',
      floor_plan: '',
      unit_location: '',
      description: ''
    });
    setIsModalOpen(true);
  };

  const handleEditClick = (unit) => {
    setIsEditMode(true);
    setSelectedUnitID(unit.unitID);
    setFloorPlanFile(null);
    setUnitLocationFile(null);
    
    const cleanStatus = (unit.status || '').toLowerCase() === 'zauzeto' ? 'prodano' : (unit.status || 'slobodno');

    setFormData({
      projectID: unit.projectID || selectedProjectID,
      unitNumber: unit.unitNumber || '',
      floor: Number(unit.floor),
      area: unit.area || '',
      bedrooms: unit.bedrooms !== null && unit.bedrooms !== undefined ? unit.bedrooms : '',
      orientation: unit.orientation || '',
      price: unit.price !== null && unit.price !== undefined ? unit.price : '',
      status: cleanStatus,
      companyID: unit.companyID || loggedInCompanyID,
      building: unit.building || '',
      reservedUntil: unit.reservedUntil !== null && unit.reservedUntil !== undefined ? unit.reservedUntil : '',
      unitTipe: unit.unitTipe || unit.unitType || 'JEDNOSOBNI STAN',
      entry: unit.entry !== null && unit.entry !== undefined ? unit.entry : '',
      hallway: unit.hallway !== null && unit.hallway !== undefined ? unit.hallway : '',
      kitchen: unit.kitchen !== null && unit.kitchen !== undefined ? unit.kitchen : '',
      livingRoom: unit.livingRoom !== null && unit.livingRoom !== undefined ? unit.livingRoom : '',
      anteroom: unit.anteroom !== null && unit.anteroom !== undefined ? unit.anteroom : '',
      bedroom1: unit.bedroom1 !== null && unit.bedroom1 !== undefined ? unit.bedroom1 : '',
      bedroom2: unit.bedroom2 !== null && unit.bedroom2 !== undefined ? unit.bedroom2 : '',
      bedroom3: unit.bedroom3 !== null && unit.bedroom3 !== undefined ? unit.bedroom3 : '',
      bedroom4: unit.bedroom4 !== null && unit.bedroom4 !== undefined ? unit.bedroom4 : '',
      bathroom1: unit.bathroom1 !== null && unit.bathroom1 !== undefined ? unit.bathroom1 : '',
      bathroom2: unit.bathroom2 !== null && unit.bathroom2 !== undefined ? unit.bathroom2 : '',
      storage: unit.storage !== null && unit.storage !== undefined ? unit.storage : '',
      garden: unit.garden !== null && unit.garden !== undefined ? unit.garden : '',
      terrace: unit.terrace !== null && unit.terrace !== undefined ? unit.terrace : '',
      balcony: unit.balcony !== null && unit.balcony !== undefined ? unit.balcony : '',
      totalIndoor: unit.totalIndoor !== null && unit.totalIndoor !== undefined ? unit.totalIndoor : '',
      totalFloorArea: unit.totalFloorArea !== null && unit.totalFloorArea !== undefined ? unit.totalFloorArea : '',
      floor_plan: unit.floor_plan || '',
      unit_location: unit.unit_location || '',
      description: unit.description || ''
    });
    setIsModalOpen(true);
  };

  const handleDeleteClick = async () => {
    if (!canEdit) return;
    const confirmed = window.confirm(`Jeste li sigurni da želite obrisati stan ${formData.unitNumber}?`);
    if (!confirmed) return;

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/unitDeleteRecord?unitID=${selectedUnitID}`, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        await saveActivity('unit_delete', `${loggedInUserName} - obrisan stan ${formData.unitNumber}`);
        await fetchUnits(selectedProjectID);
        setIsModalOpen(false);
      } else {
        alert("Došlo je do pogreške prilikom brisanja stana.");
      }
    } catch (error) {
      console.error("Greška pri brisanju:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canEdit) return;

    setLoading(true);
    const bCount = getBedroomsCountFromType(formData.unitTipe);

    const cleanNumeric = (val) => {
      if (val === undefined || val === null || String(val).trim() === '') return '0';
      return String(val).replace(',', '.').trim();
    };

    try {
      const bodyFormData = new FormData();
      bodyFormData.append('projectID', formData.projectID); 
      bodyFormData.append('unitNumber', formData.unitNumber);
      bodyFormData.append('floor', cleanNumeric(formData.floor));
      bodyFormData.append('area', cleanNumeric(formData.area));
      bodyFormData.append('bedrooms', cleanNumeric(bCount));
      bodyFormData.append('orientation', formData.orientation || '');
      bodyFormData.append('price', cleanNumeric(formData.price));
      bodyFormData.append('status', formData.status);
      bodyFormData.append('reservedUntil', cleanNumeric(formData.reservedUntil));
      bodyFormData.append('companyID', formData.companyID);
      bodyFormData.append('building', formData.building || '');
      bodyFormData.append('unitTipe', formData.unitTipe);
      bodyFormData.append('entry', cleanNumeric(formData.entry));
      bodyFormData.append('hallway', cleanNumeric(formData.hallway));
      bodyFormData.append('kitchen', cleanNumeric(formData.kitchen));
      bodyFormData.append('livingRoom', cleanNumeric(formData.livingRoom));
      bodyFormData.append('anteroom', cleanNumeric(formData.anteroom));
      bodyFormData.append('bedroom1', cleanNumeric(formData.bedroom1));
      bodyFormData.append('bedroom2', cleanNumeric(formData.bedroom2));
      bodyFormData.append('bedroom3', cleanNumeric(formData.bedroom3));
      bodyFormData.append('bedroom4', cleanNumeric(formData.bedroom4));
      bodyFormData.append('bathroom1', cleanNumeric(formData.bathroom1));
      bodyFormData.append('bathroom2', cleanNumeric(formData.bathroom2));
      bodyFormData.append('storage', cleanNumeric(formData.storage));
      bodyFormData.append('garden', cleanNumeric(formData.garden));
      bodyFormData.append('terrace', cleanNumeric(formData.terrace));
      bodyFormData.append('balcony', cleanNumeric(formData.balcony));
      bodyFormData.append('totalIndoor', cleanNumeric(formData.totalIndoor));
      bodyFormData.append('totalFloorArea', cleanNumeric(formData.totalFloorArea));
      bodyFormData.append('description', formData.description || '');
      
      const token = localStorage.getItem('token');

      if (floorPlanFile) {
        bodyFormData.append('floor_plan', floorPlanFile);
      }
      if (unitLocationFile) {
        bodyFormData.append('unit_location', unitLocationFile);
      }

      let endpoint = isEditMode ? `${API_BASE_URL}/unitUpdateRecord` : `${API_BASE_URL}/unitInsertRecord`;

      if (isEditMode) {
        bodyFormData.append('unitID', selectedUnitID);
      }

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: bodyFormData
      });

      if (response.ok) {
        const desc = isEditMode
          ? `${loggedInUserName} - ažurirani podaci za ${formData.unitNumber}`
          : `${loggedInUserName} - kreiran ${formData.unitNumber}`;
        
        await saveActivity(isEditMode ? 'unit_update' : 'unit_insert', desc);
        await fetchUnits(selectedProjectID);
        setIsModalOpen(false);
        setFloorPlanFile(null);
        setUnitLocationFile(null);
      } else {
        const errText = await response.text();
        alert(`Greška: ${errText || "Spremanje nije uspjelo."}`);
      }
    } catch (error) {
      console.error("Greška pri slanju podataka:", error);
    } finally {
      setLoading(false);
    }
  };

  const floorLayout = [1000, 4, 3, 2, 1, 0];

  const formatFloor = (floorNum) => {
    if (floorNum === 0) return "PRIZEMLJE";
    if (floorNum === 1000) return "NADGRAĐE";
    return `${floorNum}. KAT`;
  };

  const filteredUnits = units.filter(unit => {
    const matchesSearch = filterText === '' || 
      unit.unitNumber?.toLowerCase().includes(filterText.toLowerCase()) ||
      unit.area?.toString().includes(filterText);
      
    const unitStatus = (unit.status || '').toLowerCase();
    const cleanFilterStatus = filterStatus.toLowerCase();

    let matchesStatus = cleanFilterStatus === 'svi statusi';
    if (!matchesStatus) {
      if (cleanFilterStatus === 'prodano') {
        matchesStatus = unitStatus === 'prodano' || unitStatus === 'zauzeto';
      } else {
        matchesStatus = unitStatus === cleanFilterStatus;
      }
    }
      
    const matchesBedrooms = filterBedrooms === 'Bilo koji broj soba' || 
      Number(unit.bedrooms) === Number(filterBedrooms);
      
    const matchesOrientation = filterOrientation === 'Sve orijentacije' || 
      unit.orientation?.toLowerCase() === filterOrientation.toLowerCase();

    return matchesSearch && matchesStatus && matchesBedrooms && matchesOrientation;
  });

  const getStatusStyle = (status = '') => {
    const s = status.toLowerCase();
    if (s.includes('slobod')) return 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100';
    if (s.includes('rezerv')) return 'bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100';
    return 'bg-rose-50 text-rose-700 border-rose-200 hover:bg-rose-100';
  };

  const floorPlanUrl = getImageUrl(selectedUnitID, formData.floor_plan);
  const unitLocationUrl = getImageUrl(selectedUnitID, formData.unit_location);

  return (
    <div className="max-w-6xl space-y-6 pb-12">
      
      {/* 1. ZAGLAVLJE */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-extrabold text-slate-900 tracking-tight">Stanovi &amp; Poslovni prostori</h1>
        </div>
        
        <div className="flex items-center gap-2.5">
          {canEdit && (
            <button 
              type="button"
              onClick={handleAddClick}
              className="bg-slate-900 hover:bg-slate-800 text-white font-bold text-xs px-4 py-2.5 rounded-xl transition shadow-xs flex items-center gap-1.5 cursor-pointer"
            >
              <span className="text-base leading-none">+</span> Dodaj stan / prostor
            </button>
          )}

          <span className="text-xs font-bold bg-white border border-slate-200 px-3 py-2 rounded-xl text-slate-600 shadow-2xs">
            Ukupno: <strong className="text-slate-900">{filteredUnits.length}</strong>
          </span>
        </div>
      </div>

      {/* 2. UNIFICIRANA TRAKA ZA PRETRAGU I FILTERE */}
      <div className="bg-white p-3.5 rounded-2xl border border-slate-200 shadow-xs flex flex-col md:flex-row gap-3 items-center justify-between">
        <div className="relative w-full md:w-80">
          <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm pointer-events-none">
            search
          </span>
          <input 
            type="text" 
            placeholder="Pretraži po oznaci (npr. A01) ili m²..."
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
            className="w-full bg-slate-50 border border-slate-200 rounded-xl pl-9 pr-3.5 py-2 text-xs font-semibold text-slate-800 placeholder:text-slate-400 focus:bg-white focus:outline-none focus:border-slate-400 transition" 
          />
        </div>

        <div className="flex flex-wrap items-center gap-2 w-full md:w-auto">
          <select 
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="bg-slate-50 hover:bg-slate-100 border border-slate-200 rounded-xl pl-3 pr-8 py-2 text-xs font-semibold text-slate-700 cursor-pointer focus:bg-white focus:outline-none focus:border-slate-400 transition"
          >
            <option value="Svi statusi">Svi statusi</option>
            <option value="slobodno">Slobodno</option>
            <option value="rezervirano">Rezervirano</option>
            <option value="prodano">Prodano</option>
          </select>

          <select 
            value={filterBedrooms}
            onChange={(e) => setFilterBedrooms(e.target.value)}
            className="bg-slate-50 hover:bg-slate-100 border border-slate-200 rounded-xl pl-3 pr-8 py-2 text-xs font-semibold text-slate-700 cursor-pointer focus:bg-white focus:outline-none focus:border-slate-400 transition"
          >
            <option value="Bilo koji broj soba">Sve sobe</option>
            <option value={1}>1 soba</option>
            <option value={2}>2 sobe</option>
            <option value={3}>3 sobe</option>
            <option value={4}>4 sobe</option>
          </select>

          <select 
            value={filterOrientation}
            onChange={(e) => setFilterOrientation(e.target.value)}
            className="bg-slate-50 hover:bg-slate-100 border border-slate-200 rounded-xl pl-3 pr-8 py-2 text-xs font-semibold text-slate-700 cursor-pointer focus:bg-white focus:outline-none focus:border-slate-400 transition"
          >
            <option value="Sve orijentacije">Sve strane svijeta</option>
            <option value="Sjever">Sjever</option>
            <option value="Jug">Jug</option>
            <option value="Istok">Istok</option>
            <option value="Zapad">Zapad</option>
          </select>
        </div>
      </div>

      {/* 3. PRIKAZ STANOVA PO ETAŽAMA */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-xs p-6 space-y-6">
        {floorLayout.map((floorNumber) => {
          const floorUnits = filteredUnits.filter((unit) => Number(unit.floor) === floorNumber);
          if (floorUnits.length === 0 && filterText) return null;

          return (
            <div key={floorNumber} className="flex flex-col sm:flex-row sm:items-start gap-4 border-b border-slate-100 pb-5 last:border-0 last:pb-0">
              <div className="w-28 shrink-0 text-xs font-extrabold uppercase tracking-wider text-slate-400 pt-2">
                {formatFloor(floorNumber)}
              </div>
              
              <div className="flex flex-wrap gap-2.5 flex-1">
                {floorUnits.length === 0 ? (
                  <span className="text-xs text-slate-300 italic pt-2">Nema jedinica na ovoj etaži</span>
                ) : (
                  floorUnits.map((unit) => {
                    return (
                      <button 
                        key={unit.unitID} 
                        type="button"
                        onClick={() => handleEditClick(unit)}
                        className={`flex items-center gap-2 px-3.5 py-2 rounded-xl border text-xs font-bold transition shadow-2xs cursor-pointer ${getStatusStyle(unit.status)}`}
                      >
                        <span className="font-extrabold text-slate-900">{unit.unitNumber}</span>
                        <span className="font-semibold opacity-80">({unit.area} m²)</span>
                      </button>
                    );
                  })
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* 4. MODAL ZA UNOS I UREĐIVANJE */}
      {isModalOpen && (
        <div 
          onClick={() => setIsModalOpen(false)}
          className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 overflow-y-auto cursor-pointer"
        >
          <div 
            onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-2xl max-w-xl w-full shadow-2xl overflow-hidden border border-slate-100 my-8 cursor-default max-h-[90vh] flex flex-col"
          >
            <div className="p-5 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
              <div>
                <h3 className="text-base font-bold text-slate-900">
                  {isEditMode ? `Uredi: ${formData.unitNumber}` : "Dodaj novu jedinicu"}
                </h3>
              </div>
              <button 
                type="button" 
                onClick={() => setIsModalOpen(false)}
                className="text-slate-400 hover:text-slate-700 p-1.5 rounded-lg text-sm font-bold cursor-pointer"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4 overflow-y-auto flex-1">
              
              {/* 1. OSNOVNI PODACI */}
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-bold text-slate-600 mb-1">Oznaka (*)</label>
                    <input 
                      type="text" 
                      required
                      disabled={!canEdit}
                      placeholder="npr. A01 ili B07"
                      className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-xs font-semibold text-slate-800 disabled:opacity-60 focus:bg-white focus:outline-none"
                      value={formData.unitNumber}
                      onChange={(e) => setFormData({...formData, unitNumber: e.target.value})}
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-slate-600 mb-1">Zgrada / Ulaz (*)</label>
                    <input 
                      type="text" 
                      required
                      disabled={!canEdit}
                      placeholder="npr. A ili B"
                      className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-xs font-semibold text-slate-800 disabled:opacity-60 focus:bg-white focus:outline-none"
                      value={formData.building}
                      onChange={(e) => setFormData({...formData, building: e.target.value})}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-bold text-slate-600 mb-1">Etaža (*)</label>
                    <select 
                      disabled={!canEdit}
                      className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-xs font-semibold text-slate-800 disabled:opacity-60 focus:bg-white focus:outline-none cursor-pointer"
                      value={formData.floor}
                      onChange={(e) => setFormData({...formData, floor: Number(e.target.value)})}
                    >
                      <option value={1000}>Nadgrađe</option>
                      <option value={4}>4. Kat</option>
                      <option value={3}>3. Kat</option>
                      <option value={2}>2. Kat</option>
                      <option value={1}>1. Kat</option>
                      <option value={0}>Prizemlje</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-slate-600 mb-1">Status (*)</label>
                    <select 
                      disabled={!canEdit}
                      className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-xs font-semibold text-slate-800 disabled:opacity-60 focus:bg-white focus:outline-none cursor-pointer"
                      value={formData.status}
                      onChange={(e) => setFormData({...formData, status: e.target.value})}
                    >
                      <option value="slobodno">Slobodno</option>
                      <option value="rezervirano">Rezervirano</option>
                      <option value="prodano">Prodano</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-600 mb-1">Tip nekretnine</label>
                  <select 
                    disabled={!canEdit}
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-xs font-semibold text-slate-800 disabled:opacity-60 focus:bg-white focus:outline-none cursor-pointer"
                    value={formData.unitTipe}
                    onChange={(e) => setFormData({...formData, unitTipe: e.target.value})}
                  >
                    <option value="JEDNOSOBNI STAN">Jednosobni stan (1 soba)</option>
                    <option value="DVOSOBNI STAN">Dvosobni stan (2 sobe)</option>
                    <option value="TROSOBNI STAN">Trosobni stan (3 sobe)</option>
                    <option value="ČETVEROSOBNI STAN">Četverosobni stan (4 sobe)</option>
                    <option value="GARSONIJERA">Garsonijera</option>
                    <option value="POSLOVNI PROSTOR">Poslovni prostor</option>
                  </select>
                </div>
              </div>

              {/* 2. SPECIFIKACIJA PROSTORIJA */}
              <div className="border-t border-slate-100 pt-4 space-y-4">
                
                {/* A) POSLOVNI PROSTOR */}
                {isCommercial ? (
                  <div className="space-y-2.5">
                    <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 block">
                      Kvadratura poslovnog prostora (m²)
                    </span>

                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-[11px] font-medium text-slate-600 mb-1">
                          Zatvoreni prostor ukupno (m²) (*)
                        </label>
                        <input 
                          type="text" 
                          disabled={!canEdit}
                          placeholder="npr. 25.40"
                          className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-1.5 text-xs font-semibold text-slate-800 focus:bg-white focus:outline-none"
                          value={formData.totalIndoor}
                          onChange={(e) => setFormData({...formData, totalIndoor: e.target.value})}
                        />
                      </div>
                    </div>
                  </div>
                ) : (
                  /* B) STAMBENI PROSTOR */
                  <div className="space-y-2.5">
                    <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 block">
                      Unutarnji stambeni prostor (m²)
                    </span>

                    <div className="grid grid-cols-2 gap-3">
                      {/* 1. Ulaz & 2. Hodnik */}
                      <div>
                        <label className="block text-[11px] font-medium text-slate-600 mb-1">01. Ulaz (opc.)</label>
                        <input 
                          type="text" 
                          disabled={!canEdit}
                          placeholder="0.00"
                          className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-1.5 text-xs font-semibold text-slate-800 focus:bg-white focus:outline-none"
                          value={formData.entry}
                          onChange={(e) => setFormData({...formData, entry: e.target.value})}
                        />
                      </div>

                      <div>
                        <label className="block text-[11px] font-medium text-slate-600 mb-1">02. Hodnik (opc.)</label>
                        <input 
                          type="text" 
                          disabled={!canEdit}
                          placeholder="0.00"
                          className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-1.5 text-xs font-semibold text-slate-800 focus:bg-white focus:outline-none"
                          value={formData.hallway}
                          onChange={(e) => setFormData({...formData, hallway: e.target.value})}
                        />
                      </div>

                      {/* 3. Kuhinja & 4. Dnevni boravak */}
                      <div>
                        <label className="block text-[11px] font-medium text-slate-600 mb-1">03. Kuhinja (opc.)</label>
                        <input 
                          type="text" 
                          disabled={!canEdit}
                          placeholder="0.00"
                          className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-1.5 text-xs font-semibold text-slate-800 focus:bg-white focus:outline-none"
                          value={formData.kitchen}
                          onChange={(e) => setFormData({...formData, kitchen: e.target.value})}
                        />
                      </div>

                      <div>
                        <label className="block text-[11px] font-medium text-slate-600 mb-1">04. Dnevni boravak i blag. (opc.)</label>
                        <input 
                          type="text" 
                          disabled={!canEdit}
                          placeholder="0.00"
                          className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-1.5 text-xs font-semibold text-slate-800 focus:bg-white focus:outline-none"
                          value={formData.livingRoom}
                          onChange={(e) => setFormData({...formData, livingRoom: e.target.value})}
                        />
                      </div>

                      {/* 5. Predsoblje */}
                      <div>
                        <label className="block text-[11px] font-medium text-slate-600 mb-1">05. Predsoblje (opc.)</label>
                        <input 
                          type="text" 
                          disabled={!canEdit}
                          placeholder="0.00"
                          className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-1.5 text-xs font-semibold text-slate-800 focus:bg-white focus:outline-none"
                          value={formData.anteroom}
                          onChange={(e) => setFormData({...formData, anteroom: e.target.value})}
                        />
                      </div>

                      {/* 6. Spavaće sobe */}
                      {bedroomCount >= 1 && (
                        <div>
                          <label className="block text-[11px] font-medium text-slate-600 mb-1">Spavaća soba 1 (opc.)</label>
                          <input 
                            type="text" 
                            disabled={!canEdit}
                            placeholder="0.00"
                            className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-1.5 text-xs font-semibold text-slate-800 focus:bg-white focus:outline-none"
                            value={formData.bedroom1}
                            onChange={(e) => setFormData({...formData, bedroom1: e.target.value})}
                          />
                        </div>
                      )}

                      {bedroomCount >= 2 && (
                        <div>
                          <label className="block text-[11px] font-medium text-slate-600 mb-1">Spavaća soba 2 (opc.)</label>
                          <input 
                            type="text" 
                            disabled={!canEdit}
                            placeholder="0.00"
                            className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-1.5 text-xs font-semibold text-slate-800 focus:bg-white focus:outline-none"
                            value={formData.bedroom2}
                            onChange={(e) => setFormData({...formData, bedroom2: e.target.value})}
                          />
                        </div>
                      )}

                      {bedroomCount >= 3 && (
                        <div>
                          <label className="block text-[11px] font-medium text-slate-600 mb-1">Spavaća soba 3 (opc.)</label>
                          <input 
                            type="text" 
                            disabled={!canEdit}
                            placeholder="0.00"
                            className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-1.5 text-xs font-semibold text-slate-800 focus:bg-white focus:outline-none"
                            value={formData.bedroom3}
                            onChange={(e) => setFormData({...formData, bedroom3: e.target.value})}
                          />
                        </div>
                      )}

                      {bedroomCount >= 4 && (
                        <div>
                          <label className="block text-[11px] font-medium text-slate-600 mb-1">Spavaća soba 4 (opc.)</label>
                          <input 
                            type="text" 
                            disabled={!canEdit}
                            placeholder="0.00"
                            className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-1.5 text-xs font-semibold text-slate-800 focus:bg-white focus:outline-none"
                            value={formData.bedroom4}
                            onChange={(e) => setFormData({...formData, bedroom4: e.target.value})}
                          />
                        </div>
                      )}

                      {/* 7. Kupaonice */}
                      <div>
                        <label className="block text-[11px] font-medium text-slate-600 mb-1">Kupaonica 1 (opc.)</label>
                        <input 
                          type="text" 
                          disabled={!canEdit}
                          placeholder="0.00"
                          className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-1.5 text-xs font-semibold text-slate-800 focus:bg-white focus:outline-none"
                          value={formData.bathroom1}
                          onChange={(e) => setFormData({...formData, bathroom1: e.target.value})}
                        />
                      </div>

                      <div>
                        <label className="block text-[11px] font-medium text-slate-500 mb-1">Kupaonica 2 / WC (opc.)</label>
                        <input 
                          type="text" 
                          disabled={!canEdit}
                          placeholder="0.00"
                          className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-1.5 text-xs font-semibold text-slate-800 focus:bg-white focus:outline-none"
                          value={formData.bathroom2}
                          onChange={(e) => setFormData({...formData, bathroom2: e.target.value})}
                        />
                      </div>

                      {/* 8. Sprema */}
                      <div>
                        <label className="block text-[11px] font-medium text-slate-600 mb-1">Sprema / Ostava (opc.)</label>
                        <input 
                          type="text" 
                          disabled={!canEdit}
                          placeholder="0.00"
                          className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-1.5 text-xs font-semibold text-slate-800 focus:bg-white focus:outline-none"
                          value={formData.storage}
                          onChange={(e) => setFormData({...formData, storage: e.target.value})}
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* VANJSKI PROSTOR: PREDVRT (0.10), TERASA (0.25), BALKON (0.50) */}
                <div className="space-y-2.5 pt-1">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 block">
                    Vanjski prostor &amp; Dodaci (m²)
                  </span>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-[11px] font-medium text-slate-600 mb-1">
                        Predvrt / Vrt (opc. bruto)
                      </label>
                      <input 
                        type="text" 
                        disabled={!canEdit}
                        placeholder="0.00"
                        className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-1.5 text-xs font-semibold text-slate-800 focus:bg-white focus:outline-none"
                        value={formData.garden}
                        onChange={(e) => setFormData({...formData, garden: e.target.value})}
                      />
                      <span className="text-[10px] text-slate-400 mt-0.5 block">Obračunski koef. 0,10</span>
                    </div>

                    <div>
                      <label className="block text-[11px] font-medium text-slate-600 mb-1">
                        Terasa (opc. bruto)
                      </label>
                      <input 
                        type="text" 
                        disabled={!canEdit}
                        placeholder="0.00"
                        className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-1.5 text-xs font-semibold text-slate-800 focus:bg-white focus:outline-none"
                        value={formData.terrace}
                        onChange={(e) => setFormData({...formData, terrace: e.target.value})}
                      />
                      <span className="text-[10px] text-slate-400 mt-0.5 block">Obračunski koef. 0,25</span>
                    </div>

                    <div>
                      <label className="block text-[11px] font-medium text-slate-600 mb-1">
                        Balkon (opc. bruto)
                      </label>
                      <input 
                        type="text" 
                        disabled={!canEdit}
                        placeholder="0.00"
                        className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-1.5 text-xs font-semibold text-slate-800 focus:bg-white focus:outline-none"
                        value={formData.balcony}
                        onChange={(e) => setFormData({...formData, balcony: e.target.value})}
                      />
                      <span className="text-[10px] text-slate-400 mt-0.5 block">Obračunski koef. 0,50</span>
                    </div>
                  </div>
                </div>

                {/* Sažetak obračuna */}
                <div className="grid grid-cols-3 gap-2 bg-slate-50 p-3.5 rounded-xl border border-slate-200 text-center mt-3 shadow-2xs">
                  <div>
                    <span className="block text-[9px] font-bold uppercase text-slate-400">Zatvoreno</span>
                    <strong className="text-xs font-extrabold text-slate-800">
                      {parseNum(formData.totalIndoor) > 0 ? `${formData.totalIndoor} m²` : '0.00 m²'}
                    </strong>
                  </div>
                  <div>
                    <span className="block text-[9px] font-bold uppercase text-slate-400">Bruto tlocrt</span>
                    <strong className="text-xs font-extrabold text-slate-800">
                      {parseNum(formData.totalFloorArea) > 0 ? `${formData.totalFloorArea} m²` : '0.00 m²'}
                    </strong>
                  </div>
                  <div>
                    <span className="block text-[9px] font-bold uppercase text-emerald-600">Prodajna</span>
                    <strong className="text-xs font-extrabold text-emerald-700">
                      {parseNum(formData.area) > 0 ? `${formData.area} m²` : '0.00 m²'}
                    </strong>
                  </div>
                </div>
              </div>

              {/* 3. TLOCRTI I SLIKE S PAMETNIM FALLBACKOM */}
              <div className="border-t border-slate-100 pt-4 space-y-3">
                <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 block">Nacrti &amp; Slike</span>
                <div className="grid grid-cols-2 gap-3">
                  
                  {/* Tlocrt */}
                  <div className="space-y-2">
                    <label className="block text-[11px] font-medium text-slate-500">Tlocrt</label>
                    
                    {formData.floor_plan ? (
                      <div 
                        onClick={() => setPreviewImage({ url: floorPlanUrl, title: `Tlocrt ${formData.unitNumber}` })}
                        className="relative rounded-xl overflow-hidden border border-slate-200 bg-slate-50 aspect-video group cursor-zoom-in flex items-center justify-center shadow-2xs"
                        title="Klikni za pregled preko cijelog ekrana"
                      >
                        <img 
                          src={floorPlanUrl} 
                          alt="Tlocrt" 
                          onError={(e) => {
                            const gcs = getGCSUrl(selectedUnitID, formData.floor_plan);
                            if (e.target.src !== gcs) {
                              e.target.src = gcs;
                            }
                          }}
                          className="w-full h-full object-cover group-hover:scale-105 transition duration-200" 
                        />
                        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition flex items-center justify-center text-white text-[11px] font-bold gap-1">
                          <span className="material-symbols-outlined text-sm">zoom_in</span> Povećaj
                        </div>
                      </div>
                    ) : (
                      <div className="h-16 rounded-xl border border-dashed border-slate-200 bg-slate-50/60 flex items-center justify-center text-[10px] text-slate-400 italic">
                        Nema učitanog tlocrta
                      </div>
                    )}

                    <input 
                      type="file" 
                      accept="image/*"
                      disabled={!canEdit}
                      onChange={(e) => setFloorPlanFile(e.target.files[0])}
                      className="w-full text-xs text-slate-500 file:mr-2 file:py-1 file:px-2.5 file:rounded-lg file:border-0 file:text-xs file:font-bold file:bg-slate-100 file:text-slate-700 hover:file:bg-slate-200"
                    />
                  </div>

                  {/* Lokacija na etaži */}
                  <div className="space-y-2">
                    <label className="block text-[11px] font-medium text-slate-500">Lokacija na etaži</label>
                    
                    {formData.unit_location ? (
                      <div 
                        onClick={() => setPreviewImage({ url: unitLocationUrl, title: `Lokacija na etaži - ${formData.unitNumber}` })}
                        className="relative rounded-xl overflow-hidden border border-slate-200 bg-slate-50 aspect-video group cursor-zoom-in flex items-center justify-center shadow-2xs"
                        title="Klikni za pregled preko cijelog ekrana"
                      >
                        <img 
                          src={unitLocationUrl} 
                          alt="Lokacija na etaži" 
                          onError={(e) => {
                            const gcs = getGCSUrl(selectedUnitID, formData.unit_location);
                            if (e.target.src !== gcs) {
                              e.target.src = gcs;
                            }
                          }}
                          className="w-full h-full object-cover group-hover:scale-105 transition duration-200" 
                        />
                        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition flex items-center justify-center text-white text-[11px] font-bold gap-1">
                          <span className="material-symbols-outlined text-sm">zoom_in</span> Povećaj
                        </div>
                      </div>
                    ) : (
                      <div className="h-16 rounded-xl border border-dashed border-slate-200 bg-slate-50/60 flex items-center justify-center text-[10px] text-slate-400 italic">
                        Nema učitane lokacije
                      </div>
                    )}

                    <input 
                      type="file" 
                      accept="image/*"
                      disabled={!canEdit}
                      onChange={(e) => setUnitLocationFile(e.target.files[0])}
                      className="w-full text-xs text-slate-500 file:mr-2 file:py-1 file:px-2.5 file:rounded-lg file:border-0 file:text-xs file:font-bold file:bg-slate-100 file:text-slate-700 hover:file:bg-slate-200"
                    />
                  </div>

                </div>
              </div>

              {/* 4. CIJENA I ORIJENTACIJA */}
              <div className="border-t border-slate-100 pt-4 grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[11px] font-medium text-slate-500 mb-1">Cijena (€)</label>
                  <input 
                    type="text" 
                    disabled={!canEdit}
                    placeholder="0.00"
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-1.5 text-xs font-semibold text-slate-800 focus:bg-white focus:outline-none"
                    value={formData.price}
                    onChange={(e) => setFormData({...formData, price: e.target.value})}
                  />
                </div>

                <div>
                  <label className="block text-[11px] font-medium text-slate-500 mb-1">Orijentacija</label>
                  <input 
                    type="text" 
                    disabled={!canEdit}
                    placeholder="npr. Jug"
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-1.5 text-xs font-semibold text-slate-800 focus:bg-white focus:outline-none"
                    value={formData.orientation} 
                    onChange={(e) => setFormData({...formData, orientation: e.target.value})} 
                  />
                </div>
              </div>

              {/* 5. GUMBI NA DNU MODALA */}
              <div className="flex justify-between items-center pt-4 border-t border-slate-100">
                <div>
                  {isEditMode && canEdit && (
                    <button 
                      type="button"
                      onClick={handleDeleteClick}
                      className="text-xs font-bold text-rose-600 hover:text-rose-800 transition cursor-pointer"
                    >
                      Obriši
                    </button>
                  )}
                </div>
                
                <div className="flex gap-2">
                  <button 
                    type="button" 
                    onClick={() => setIsModalOpen(false)}
                    className="bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-xs px-4 py-2 rounded-xl transition cursor-pointer"
                  >
                    Odustani
                  </button>
                  {canEdit && (
                    <button 
                      type="submit"
                      disabled={loading}
                      className="bg-slate-900 hover:bg-slate-800 text-white font-bold text-xs px-5 py-2 rounded-xl transition shadow-xs disabled:opacity-50 cursor-pointer"
                    >
                      {loading ? "Spremanje..." : (isEditMode ? "Spremi izmjene" : "Spremi")}
                    </button>
                  )}
                </div>
              </div>

            </form>
          </div>
        </div>
      )}

      {/* 5. FULLSCREEN LIGHTBOX PREVIEW */}
      {previewImage && (
        <div 
          className="fixed inset-0 bg-slate-950/85 z-[100] flex items-center justify-center p-4 backdrop-blur-sm cursor-pointer"
          onClick={() => setPreviewImage(null)}
        >
          <div 
            className="relative max-w-4xl w-full max-h-[90vh] flex flex-col items-center cursor-default" 
            onClick={(e) => e.stopPropagation()}
          >
            <div className="w-full flex items-center justify-between text-white mb-3 px-1">
              <span className="text-sm font-bold tracking-tight">{previewImage.title}</span>
              <button 
                type="button"
                onClick={() => setPreviewImage(null)}
                className="bg-white/10 hover:bg-white/20 text-white rounded-full p-2 text-xs font-bold transition cursor-pointer flex items-center justify-center"
              >
                ✕
              </button>
            </div>
            
            <div className="bg-white/5 rounded-2xl p-2 border border-white/10 overflow-hidden flex items-center justify-center max-h-[82vh]">
              <img 
                src={previewImage.url} 
                alt="Pregled nacrta" 
                className="max-w-full max-h-[78vh] object-contain rounded-xl shadow-2xl" 
              />
            </div>
          </div>
        </div>
      )}

    </div>
  );
}